/**
 * BulkAddRowPreview — single row in Block 1 of the preview pane (Block 6.E).
 *
 * Compact row showing the parsed values + ✓/⚠ status indicator. Errors
 * surface inline.
 */

import type { ReactNode } from 'react';
import { AlertTriangle, Ban, X, Check } from 'lucide-react';
import type { BulkAddParsedRowPreview } from '@/lib/types';

export interface BulkAddRowPreviewProps {
  row: BulkAddParsedRowPreview;
  /** Disposition reflected back from the parent (skip_as_duplicate when matching dup chosen). */
  disposition: 'create' | 'skip_as_duplicate' | 'skip_with_error';
}

export function BulkAddRowPreview({ row, disposition }: BulkAddRowPreviewProps): ReactNode {
  const hasErrors = row.errors.length > 0;
  // X.6.7B — Lucide status icon (was ⚠/⊘/✗/✓ glyphs).
  const Indicator = hasErrors
    ? AlertTriangle
    : disposition === 'skip_as_duplicate'
      ? Ban
      : disposition === 'skip_with_error'
        ? X
        : Check;
  const indicatorColor = hasErrors || disposition === 'skip_with_error'
    ? 'text-warm-600'
    : disposition === 'skip_as_duplicate'
      ? 'text-ink-soft'
      : 'text-sage-600';

  const summary =
    [
      row.name ?? '(no name)',
      row.category_slug ?? '(no category)',
      row.kind_raw ?? 'other',
    ].join(' · ');

  return (
    <li className="text-sm py-1 border-b border-sage-800/[8%] last:border-b-0">
      <div className="flex items-baseline gap-2">
        <Indicator size={14} aria-hidden="true" className={`shrink-0 self-center ${indicatorColor}`} />
        <span className={hasErrors ? 'text-red-700' : 'text-sage-800'}>{summary}</span>
        <span className="text-xs text-ink-soft ml-auto">row {row.row_index + 1}</span>
      </div>
      {hasErrors && (
        <ul className="mt-1 ml-6 text-xs text-red-700 list-disc">
          {row.errors.map((err, i) => (
            <li key={i}>{err}</li>
          ))}
        </ul>
      )}
    </li>
  );
}
